import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DownloadOutlined,
  SearchOutlined,
  CloseOutlined,
  DownOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { Modal, Tabs } from 'antd';
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { getAllQuarters, getQuarterlyUsers } from '../../../services/reviewsServices';
import { getObjectivesReport } from '../../../services/objectiveServices';
import { NotificationAlert, Status } from '../../../components';
import { AllReviewsProps, quarterlyUsers } from '../../../types';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, ChartLegend, Filler);

/* Theme */
const C = {
  ink: '#191F1B',
  paper: '#FFFFFF',
  card: '#FAF9F6',
  line: '#E4E1D7',
  soft: '#EEECE5',
  gray: '#8A857A',
  gray2: '#6C7370',
  grayFaint: '#B0ABA0',
  green: '#15805A',
  greenDark: '#0E5C40',
  greenSoft: '#E2F0E9',
  amber: '#E2A33B',
  amberText: '#B07E1E',
  amberSoft: '#F8EEDC',
  red: '#C0452C',
  redSoft: '#F6E3DE',
  greyBand: '#C9C3B8',
  greySoft: '#EFEDE8',
};

type Band = 'Completed' | 'In Progress' | 'Not Started' | 'No mentor';

const BAND: Record<Band, { full: string; dark: string; pillBg: string; pillText: string }> = {
  Completed: { full: C.green, dark: C.greenDark, pillBg: C.greenSoft, pillText: C.green },
  'In Progress': { full: C.amber, dark: C.amberText, pillBg: C.amberSoft, pillText: C.amberText },
  'Not Started': { full: C.greyBand, dark: '#A8A296', pillBg: C.greySoft, pillText: C.gray2 },
  'No mentor': { full: C.red, dark: '#8F301D', pillBg: C.redSoft, pillText: C.red },
};
// 'No mentor' is intentionally excluded — mentor-less users only exist in dev data
const BAND_ORDER: Band[] = ['Completed', 'In Progress', 'Not Started'];

/* Objective categories (from the Create Objective form) */
const CATEGORIES = ['Organizational', 'Project Based', 'Personal Development'];
const CAT_STYLE: Record<string, { color: string; dark: string; soft: string; text: string }> = {
  Organizational: { color: C.green, dark: C.greenDark, soft: C.greenSoft, text: C.green },
  'Project Based': { color: '#3E6DA6', dark: '#294B72', soft: '#E7EDF5', text: '#345C8C' },
  'Personal Development': { color: C.amber, dark: C.amberText, soft: C.amberSoft, text: C.amberText },
};
const catStyle = (c: string) =>
  CAT_STYLE[c] || { color: C.greyBand, dark: '#8F897D', soft: C.greySoft, text: C.gray2 };

/* Employee shape (mapped from the quarterly-user-status API) */
interface Employee {
  name: string;
  designation: string;
  tier: number | string;
  mentor: string;
  objectives: number;
  mentorStatus: Exclude<Band, 'No mentor'>;
  menteeStatus: Exclude<Band, 'No mentor'>;
  mentorSubmissionDate: string | null;
  menteeSubmissionDate: string | null;
  // "All quarters" mode: was this role late in at least one quarter
  mentorLateAny?: boolean;
  menteeLateAny?: boolean;
}

const hasMentor = (mentor: string) => !!mentor && mentor !== '-';

// a submission is late when it lands on a later calendar day than its deadline
// (same-day counts as on time). Not-yet-submitted (null) is never "late" here.
const isLateSubmission = (submission?: string | null, deadline?: string) =>
  !!submission && !!deadline && dayjs(submission).isAfter(dayjs(deadline), 'day');

const normalizeStatus = (raw?: string): Exclude<Band, 'No mentor'> => {
  const s = (raw || '').toUpperCase();
  if (s === 'COMPLETED') return 'Completed';
  if (s === 'IN_PROGRESS' || s === 'IN_PROGRESS_AT_RISK' || s === 'SUBMITTED_TO_HR')
    return 'In Progress';
  return 'Not Started';
};

const toEmployee = (u: quarterlyUsers): Employee => ({
  name: u.user.name,
  designation: u.user.designation,
  tier: u.user.tier,
  mentor: u.user.mentor?.name || '-',
  objectives: u.user.objectiveCount || 0,
  mentorStatus: normalizeStatus(u.mentorStatus),
  menteeStatus: normalizeStatus(u.menteeStatus),
  mentorSubmissionDate: u.mentorSubmissionDate,
  menteeSubmissionDate: u.menteeSubmissionDate,
});

const round = (n: number, d: number) => (d ? Math.round((n / d) * 100) : 0);

// "IN_PROGRESS" -> "In Progress", "NOT_COMPLETED_EXPIRED" -> "Not Completed Expired"
const prettyStatus = (s: string) =>
  (s || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase()) || '—';

// "All quarters" sentinel for the quarter dropdown
const ALL_QUARTERS = 'ALL';

// pick the more-complete of two statuses (Completed beats In Progress beats Not Started)
const bestStatus = (
  a: Exclude<Band, 'No mentor'>,
  b: Exclude<Band, 'No mentor'>
): Exclude<Band, 'No mentor'> => (BAND_ORDER.indexOf(a) <= BAND_ORDER.indexOf(b) ? a : b);

// merge several quarters' rosters into one row per employee (used for "All quarters")
const aggregateQuarters = (
  qs: AllReviewsProps[],
  results: { data?: quarterlyUsers[] }[]
): Employee[] => {
  const byName = new Map<string, Employee>();
  results.forEach((res, i) => {
    const q = qs[i];
    (res.data || []).forEach((u) => {
      const e = toEmployee(u);
      const mLate = isLateSubmission(e.mentorSubmissionDate, q.mentorDeadline);
      const eLate = isLateSubmission(e.menteeSubmissionDate, q.menteeDeadline);
      const prev = byName.get(e.name);
      if (!prev) {
        byName.set(e.name, { ...e, mentorLateAny: mLate, menteeLateAny: eLate });
        return;
      }
      prev.objectives = Math.max(prev.objectives, e.objectives);
      prev.mentorStatus = bestStatus(prev.mentorStatus, e.mentorStatus);
      prev.menteeStatus = bestStatus(prev.menteeStatus, e.menteeStatus);
      prev.mentorLateAny = prev.mentorLateAny || mLate;
      prev.menteeLateAny = prev.menteeLateAny || eLate;
      if (
        e.mentorSubmissionDate &&
        (!prev.mentorSubmissionDate ||
          dayjs(e.mentorSubmissionDate).isAfter(dayjs(prev.mentorSubmissionDate)))
      )
        prev.mentorSubmissionDate = e.mentorSubmissionDate;
      if (
        e.menteeSubmissionDate &&
        (!prev.menteeSubmissionDate ||
          dayjs(e.menteeSubmissionDate).isAfter(dayjs(prev.menteeSubmissionDate)))
      )
        prev.menteeSubmissionDate = e.menteeSubmissionDate;
    });
  });
  return [...byName.values()];
};

interface Metrics {
  total: number;
  mentorBands: Record<Band, number>;
  menteeBands: Record<Band, number>;
  mentorsPct: number;
  menteesPct: number;
  haveObjectives: number;
  noObjectives: number;
  coveragePct: number;
}

function computeMetrics(emps: Employee[]): Metrics {
  const total = emps.length;
  const bandsFor = (role: 'mentor' | 'mentee'): Record<Band, number> => {
    const c: Record<Band, number> = { Completed: 0, 'In Progress': 0, 'Not Started': 0, 'No mentor': 0 };
    emps.forEach((e) => {
      if (role === 'mentor' && !hasMentor(e.mentor)) {
        c['No mentor']++;
        return;
      }
      c[role === 'mentor' ? e.mentorStatus : e.menteeStatus]++;
    });
    return c;
  };
  const mentorBands = bandsFor('mentor');
  const menteeBands = bandsFor('mentee');
  const haveObjectives = emps.filter((e) => e.objectives > 0).length;
  return {
    total,
    mentorBands,
    menteeBands,
    mentorsPct: round(mentorBands.Completed, total),
    menteesPct: round(menteeBands.Completed, total),
    haveObjectives,
    noObjectives: total - haveObjectives,
    coveragePct: round(haveObjectives, total),
  };
}

/* ============================ 3D pie (generic, clickable, solid/opaque) ============================ */
const PIE = { cx: 190, cy: 116, rx: 148, ry: 90, depth: 26 };
const piePt = (t: number): [number, number] => [
  PIE.cx + PIE.rx * Math.cos(t),
  PIE.cy + PIE.ry * Math.sin(t),
];
const fx = (n: number) => n.toFixed(2);

const pieArc = (a0: number, a1: number, dy: number, sweep: 0 | 1) => {
  let d = '';
  if (sweep === 1) {
    let s = a0;
    while (s < a1 - 1e-9) {
      const e = Math.min(s + Math.PI * 0.999, a1);
      const [x, y] = piePt(e);
      d += `A${fx(PIE.rx)} ${fx(PIE.ry)} 0 0 1 ${fx(x)} ${fx(y + dy)} `;
      s = e;
    }
  } else {
    let s = a1;
    while (s > a0 + 1e-9) {
      const e = Math.max(s - Math.PI * 0.999, a0);
      const [x, y] = piePt(e);
      d += `A${fx(PIE.rx)} ${fx(PIE.ry)} 0 0 0 ${fx(x)} ${fx(y + dy)} `;
      s = e;
    }
  }
  return d;
};

function pieSlicePaths(a0: number, a1: number) {
  const [sx, sy] = piePt(a0);
  const top = `M${fx(PIE.cx)} ${fx(PIE.cy)} L${fx(sx)} ${fx(sy)} ${pieArc(a0, a1, 0, 1)}Z`;
  const walls: string[] = [];
  for (let k = 0; k <= 1; k++) {
    const lo = Math.max(a0, k * 2 * Math.PI);
    const hi = Math.min(a1, k * 2 * Math.PI + Math.PI);
    if (hi > lo + 1e-6) {
      const [x1, y1] = piePt(lo);
      const [x2, y2] = piePt(hi);
      walls.push(
        `M${fx(x1)} ${fx(y1)} ${pieArc(lo, hi, 0, 1)}L${fx(x2)} ${fx(y2 + PIE.depth)} ` +
          `${pieArc(lo, hi, PIE.depth, 0)}Z`
      );
    }
  }
  const radial = (t: number) => {
    const [x, y] = piePt(t);
    return (
      `M${fx(PIE.cx)} ${fx(PIE.cy)} L${fx(x)} ${fx(y)} L${fx(x)} ${fx(y + PIE.depth)} ` +
      `L${fx(PIE.cx)} ${fx(PIE.cy + PIE.depth)} Z`
    );
  };
  return { top, walls, radials: [radial(a0), radial(a1)] };
}

interface PieSliceData {
  name: string;
  value: number;
  color: string;
  dark: string;
}

const Pie3D = ({
  slices,
  unit,
  hot,
  active,
  excludeMode = false,
  onHot,
  onClick,
  onBackgroundClick,
}: {
  slices: PieSliceData[];
  unit: string;
  hot: string | null;
  active: string[];
  excludeMode?: boolean;
  onHot: (b: string | null) => void;
  onClick: (b: string) => void;
  onBackgroundClick?: () => void;
}) => {
  const [prog, setProg] = useState(0);
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const dataKey = slices.map((s) => `${s.name}:${s.value}`).join('|');
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / 1000);
      setProg(1 - Math.pow(1 - p, 3));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [dataKey]);

  const sum = slices.reduce((s, x) => s + x.value, 0) || 1;
  let acc = -Math.PI / 2;
  const rendered = slices.map((s) => {
    const frac = s.value / sum;
    const a0 = acc;
    const a1 = acc + frac * 2 * Math.PI * prog;
    acc += frac * 2 * Math.PI;
    return { ...s, a0, a1, mid: (a0 + a1) / 2, frac };
  });

  const hotSlice = hot ? slices.find((s) => s.name === hot) : null;

  return (
    <div ref={wrapRef} className="relative shrink-0" style={{ width: 360 }}>
      <svg
        viewBox="0 0 380 250"
        style={{ width: '100%', display: 'block', overflow: 'visible' }}
        onClick={() => onBackgroundClick?.()}
        onMouseLeave={() => {
          setTip(null);
          onHot(null);
        }}
        onMouseMove={(e) => {
          const r = wrapRef.current?.getBoundingClientRect();
          if (r) setTip({ x: e.clientX - r.left, y: e.clientY - r.top });
        }}
      >
        <ellipse
          cx={PIE.cx}
          cy={PIE.cy + PIE.depth + 12}
          rx={PIE.rx * 1.02}
          ry={PIE.ry * 0.6}
          fill="rgba(25,31,27,0.08)"
        />
        {/* Pass 1 — every slice's 3D sides (walls + radials). Drawn first so no
            side ever paints over a top face → solid, opaque, no colour bleed. */}
        {rendered.map((s) => {
          if (s.a1 - s.a0 < 1e-5) return null;
          const { walls, radials } = pieSlicePaths(s.a0, s.a1);
          const isHot = hot === s.name;
          const isActive = active.includes(s.name);
          const excluded = excludeMode && isActive;
          const included = !excludeMode && isActive;
          const dimOthers = !excludeMode && (hot || active.length > 0) && !isHot && !isActive;
          const lift =
            included || isHot
              ? `translate(${fx(Math.cos(s.mid) * 13)} ${fx((Math.sin(s.mid) * 13 * PIE.ry) / PIE.rx - 5)})`
              : undefined;
          return (
            <g
              key={`side-${s.name}`}
              transform={lift}
              style={{
                transition: 'transform .28s cubic-bezier(.22,1,.36,1), opacity .2s',
                opacity: excluded ? 0.25 : dimOthers ? 0.45 : 1,
                pointerEvents: 'none',
              }}
            >
              {radials.map((d, i) => (
                <path key={`r${i}`} d={d} fill={s.dark} />
              ))}
              {walls.map((d, i) => (
                <path key={`w${i}`} d={d} fill={s.dark} />
              ))}
            </g>
          );
        })}
        {/* Pass 2 — every slice's top face + label, on top of all the sides. */}
        {rendered.map((s) => {
          if (s.a1 - s.a0 < 1e-5) return null;
          const { top } = pieSlicePaths(s.a0, s.a1);
          const isHot = hot === s.name;
          const isActive = active.includes(s.name);
          const excluded = excludeMode && isActive;
          const included = !excludeMode && isActive;
          const dimOthers = !excludeMode && (hot || active.length > 0) && !isHot && !isActive;
          const lift =
            included || isHot
              ? `translate(${fx(Math.cos(s.mid) * 13)} ${fx((Math.sin(s.mid) * 13 * PIE.ry) / PIE.rx - 5)})`
              : undefined;
          const lx = PIE.cx + PIE.rx * 0.62 * Math.cos(s.mid);
          const ly = PIE.cy + PIE.ry * 0.62 * Math.sin(s.mid);
          return (
            <g
              key={s.name}
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => onHot(s.name)}
              onClick={(evt) => {
                evt.stopPropagation();
                onClick(s.name);
              }}
            >
              <g
                transform={lift}
                style={{
                  transition: 'transform .28s cubic-bezier(.22,1,.36,1), opacity .2s',
                  opacity: excluded ? 0.3 : dimOthers ? 0.45 : 1,
                }}
              >
                <path
                  d={top}
                  fill={s.color}
                  stroke={excluded ? C.red : included ? C.ink : C.paper}
                  strokeWidth={excluded || included ? 2.5 : 1.5}
                  strokeDasharray={excluded ? '5 4' : undefined}
                  strokeLinejoin="round"
                />
                {prog > 0.98 && s.frac > 0.07 && (
                  <text
                    x={lx}
                    y={ly + 4}
                    textAnchor="middle"
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      fill: '#fff',
                      pointerEvents: 'none',
                      textDecoration: excluded ? 'line-through' : undefined,
                    }}
                  >
                    {s.value}
                  </text>
                )}
              </g>
            </g>
          );
        })}
      </svg>
      {hotSlice && tip && (
        <div
          className="pointer-events-none absolute z-10"
          style={{
            left: Math.min(tip.x + 16, 190),
            top: tip.y + 14,
            background: C.ink,
            color: C.paper,
            borderRadius: 12,
            padding: '10px 13px',
            fontSize: 12.5,
            lineHeight: 1.5,
            boxShadow: '0 10px 30px rgba(25,31,27,.28)',
            maxWidth: 230,
          }}
        >
          <div style={{ fontWeight: 700 }}>{hotSlice.name}</div>
          <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-.02em' }}>
            {hotSlice.value}{' '}
            <span style={{ fontSize: 11.5, fontWeight: 600, color: C.grayFaint }}>{unit}</span>
          </div>
          <div style={{ fontSize: 11, color: C.grayFaint }}>click to filter the list below</div>
        </div>
      )}
    </div>
  );
};

/* UI atoms */
const Pill = ({ bg, text, children }: { bg: string; text: string; children: React.ReactNode }) => (
  <span
    className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
    style={{ background: bg, color: text }}
  >
    {children}
  </span>
);

const BandPill = ({ status }: { status: Band }) => {
  const b = BAND[status];
  return (
    <Pill bg={b.pillBg} text={b.pillText}>
      {status}
    </Pill>
  );
};

const CatChip = ({ cat }: { cat: string }) => {
  const s = catStyle(cat);
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ background: s.soft, color: s.text }}
    >
      <span className="h-[7px] w-[7px] rounded-full" style={{ background: s.color }} />
      {cat}
    </span>
  );
};

// small red "Late" badge with a submitted-vs-deadline tooltip
const Dropdown = ({
  value,
  onChange,
  options,
  label,
  width,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  label: (v: string) => string;
  width?: number;
}) => (
  <div className="relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="cursor-pointer appearance-none truncate rounded-full border-none py-2 pl-4 pr-9 text-[12.5px] font-semibold outline-none"
      style={{ background: C.soft, color: C.ink, maxWidth: width }}
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {label(o)}
        </option>
      ))}
    </select>
    <DownOutlined
      style={{ color: C.gray, fontSize: 11 }}
      className="pointer-events-none absolute right-3.5 top-3"
    />
  </div>
);

const MultiDropdown = ({
  selected,
  onToggle,
  onClear,
  options,
  allLabel,
  itemLabel,
}: {
  selected: string[];
  onToggle: (v: string) => void;
  onClear: () => void;
  options: string[];
  allLabel: string;
  itemLabel: (v: string) => string;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const summary =
    selected.length === 0
      ? allLabel
      : selected.length <= 2
        ? selected.map(itemLabel).join(', ')
        : `${selected.length} selected`;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="cursor-pointer truncate rounded-full border-none py-2 pl-4 pr-9 text-[12.5px] font-semibold outline-none"
        style={{ background: C.soft, color: C.ink }}
      >
        {summary}
      </button>
      <DownOutlined
        style={{ color: C.gray, fontSize: 11 }}
        className="pointer-events-none absolute right-3.5 top-3"
      />
      {open && (
        <div
          className="absolute left-0 top-[calc(100%+6px)] z-20 min-w-[160px] rounded-2xl border p-1.5"
          style={{ background: C.paper, borderColor: C.line, boxShadow: '0 10px 30px rgba(25,31,27,.14)' }}
        >
          <button
            onClick={onClear}
            className="flex w-full items-center rounded-xl px-3 py-1.5 text-left text-[12.5px] font-semibold"
            style={{
              background: 'transparent',
              color: selected.length === 0 ? C.ink : C.gray2,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {allLabel}
          </button>
          {options.map((o) => {
            const on = selected.includes(o);
            return (
              <button
                key={o}
                onClick={() => onToggle(o)}
                className="flex w-full items-center gap-2 rounded-xl px-3 py-1.5 text-left text-[12.5px] font-semibold"
                style={{
                  background: on ? C.soft : 'transparent',
                  color: C.ink,
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <span
                  className="flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded"
                  style={{
                    background: on ? C.green : C.paper,
                    border: on ? 'none' : `1.5px solid ${C.greyBand}`,
                    color: '#fff',
                    fontSize: 9,
                    fontWeight: 700,
                  }}
                >
                  {on ? '✓' : ''}
                </span>
                {itemLabel(o)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

type FilterItem = { role: 'mentor' | 'mentee'; status: Band };

const StatusBar = ({
  role,
  label,
  bands,
  filter,
  onToggle,
}: {
  role: 'mentor' | 'mentee';
  label: string;
  bands: Record<Band, number>;
  filter: FilterItem[];
  onToggle: (role: 'mentor' | 'mentee', status: Band) => void;
}) => {
  const segs = BAND_ORDER.map((s) => ({ s, c: bands[s] || 0 })).filter((x) => x.c > 0);
  // width against visible segments only, so excluded bands (e.g. 'No mentor') leave no gap
  const segTotal = segs.reduce((sum, x) => sum + x.c, 0) || 1;
  // every segment gets at least MIN_W% so its "count (pct%)" label fits;
  // the remaining segments share the leftover width proportionally
  const MIN_W = 11;
  const raw = segs.map((x) => (x.c / segTotal) * 100);
  const reserved = raw.reduce((sum, p) => sum + (p < MIN_W ? MIN_W : 0), 0);
  const largeTotal = raw.reduce((sum, p) => sum + (p < MIN_W ? 0 : p), 0) || 1;
  const widths = raw.map((p) => (p < MIN_W ? MIN_W : (p / largeTotal) * (100 - reserved)));
  const roleHasSelection = filter.some((f) => f.role === role);
  return (
    <div className="flex items-center gap-4">
      <div className="w-32 shrink-0 text-sm font-semibold" style={{ color: C.ink }}>
        {label}
      </div>
      <div
        className="flex flex-1 items-stretch overflow-hidden"
        style={{ height: 52, borderRadius: 12, background: C.soft }}
      >
        {segs.map(({ s, c }, i) => {
          const pct = Math.round((c / segTotal) * 100);
          const active = filter.some((f) => f.role === role && f.status === s);
          const dim = roleHasSelection && !active;
          const b = BAND[s];
          return (
            <button
              key={s}
              onClick={() => onToggle(role, s)}
              title={`${s} — ${c} (${pct}%) · click to filter`}
              className="flex items-center justify-center overflow-hidden whitespace-nowrap text-[13px] font-bold transition-all hover:brightness-105"
              style={{
                width: `${widths[i]}%`,
                background: b.full,
                color: s === 'Not Started' ? C.gray2 : '#fff',
                filter: dim ? 'grayscale(.4) opacity(.55)' : 'none',
                border: 'none',
                cursor: 'pointer',
                boxShadow: active ? `inset 0 0 0 3px ${C.ink}` : 'none',
                borderRadius: active ? 8 : 0,
              }}
            >
              {c} ({pct}%)
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ============================ Submission trend (line chart) ============================ */
interface TrendPoint {
  id: string;
  name: string;
  mentorPct: number;
  menteePct: number;
  total: number;
}

const TrendChart = ({ data }: { data: TrendPoint[] }) => {
  const chartData = {
    labels: data.map((d) => d.name),
    datasets: [
      {
        label: 'Mentor',
        data: data.map((d) => d.mentorPct),
        borderColor: C.green,
        backgroundColor: 'rgba(21,128,90,0.08)',
        pointBackgroundColor: C.green,
        pointBorderColor: '#fff',
        pointBorderWidth: 1.5,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2.5,
        tension: 0.35,
        fill: true,
      },
      {
        label: 'Mentee',
        data: data.map((d) => d.menteePct),
        borderColor: '#3E6DA6',
        backgroundColor: 'rgba(62,109,166,0.06)',
        pointBackgroundColor: '#3E6DA6',
        pointBorderColor: '#fff',
        pointBorderWidth: 1.5,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2.5,
        tension: 0.35,
        fill: true,
      },
    ],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 7,
          color: C.gray2,
          font: { size: 12, family: 'Roboto' },
        },
      },
      tooltip: {
        backgroundColor: C.ink,
        padding: 12,
        cornerRadius: 10,
        titleFont: { size: 12, family: 'Roboto' },
        bodyFont: { size: 12.5, family: 'Roboto' },
        callbacks: { label: (ctx: any) => ` ${ctx.dataset.label}: ${ctx.parsed.y}% completed` },
      },
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        ticks: { stepSize: 25, callback: (v: any) => `${v}%`, color: C.grayFaint, font: { size: 11 } },
        grid: { color: C.soft },
        border: { display: false },
      },
      x: {
        ticks: { color: C.gray2, font: { size: 11.5, family: 'Roboto' } },
        grid: { display: false },
        border: { display: false },
      },
    },
  };
  return (
    <div style={{ height: 240 }}>
      <Line data={chartData} options={options as any} />
    </div>
  );
};

type View = 'objectives' | 'feedback';

// submission-trend section is hidden for now; flip to true to bring it back
const SHOW_TREND = false;

const FeedbackDashboard = () => {
  const myRef = useRef<any>();
  const [view, setView] = useState<View>('objectives');
  const [quarters, setQuarters] = useState<AllReviewsProps[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedId, setSelectedId] = useState<string>('');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [reportRows, setReportRows] = useState<
    { name: string; category: string; description: string; status: string }[]
  >([]);
  const [objModal, setObjModal] = useState<{
    name: string;
    objectives: { description: string; category: string; status: string }[];
  } | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const scrollToTable = () => tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const [loading, setLoading] = useState(true);

  // shared filters
  const [tierSel, setTierSel] = useState<string[]>([]); // empty = all tiers
  const [desigSel, setDesigSel] = useState<string[]>([]); // empty = all designations
  const [mentorSel, setMentorSel] = useState<string[]>([]); // empty = all mentors
  const [q, setQ] = useState('');

  // select mode — checkboxes appear so specific employees can be exported
  const [selectMode, setSelectMode] = useState(false);
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const toggleSelectMode = () =>
    setSelectMode((on) => {
      if (on) setSelectedNames([]);
      return !on;
    });
  const toggleRowSel = (name: string) =>
    setSelectedNames((sel) =>
      sel.includes(name) ? sel.filter((n) => n !== name) : [...sel, name]
    );

  // chart-driven drill-down filters (one per view)
  // objFilter holds selected categories (multi-select), or exactly ['has'] / ['none']
  const [objFilter, setObjFilter] = useState<string[]>([]);
  const [catMode, setCatMode] = useState<'has' | 'without'>('has'); // has vs. missing selected categories
  const [fbFilter, setFbFilter] = useState<FilterItem[]>([]);
  const [lateSel, setLateSel] = useState(false); // feedback view: show only late submitters
  const [hotSlice, setHotSlice] = useState<string | null>(null);

  // table sorting (shared across both views)
  type SortKey =
    | 'name'
    | 'designation'
    | 'tier'
    | 'mentor'
    | 'categories'
    | 'objectives'
    | 'mentorFb'
    | 'menteeFb';
  // null = default (natural) order. Clicking a header cycles asc → desc → default.
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(null);
  const toggleSort = (key: SortKey) =>
    setSort((cur) => {
      if (!cur || cur.key !== key) return { key, dir: 'asc' };
      if (cur.dir === 'asc') return { key, dir: 'desc' };
      return null; // third click clears the sort back to default
    });

  // submission trend (feedback view)
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const trendBuilt = useRef(false);

  const meta = quarters.find((c) => c.id === selectedId);

  const quarterYear = (c: AllReviewsProps) => String(dayjs(c.startDate).year());

  const years = useMemo(
    () => Array.from(new Set(quarters.map(quarterYear))).sort((a, b) => Number(b) - Number(a)),
    [quarters]
  );

  const yearQuarters = useMemo(
    () =>
      quarters
        .filter((c) => quarterYear(c) === selectedYear)
        .sort((a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf()),
    [quarters, selectedYear]
  );

  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    if (selectedId === ALL_QUARTERS) return; // stay in "All quarters" for the newly picked year
    const inYear = quarters
      .filter((c) => quarterYear(c) === year)
      .sort((a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf());
    const next = inYear.find((c) => c.status === 1) || inYear[0];
    if (next) setSelectedId(next.id);
  };

  useEffect(() => {
    (async () => {
      const [quartersRes, reportRes] = await Promise.all([
        getAllQuarters('hr'),
        getObjectivesReport(),
      ]);
      if (reportRes.data) {
        setReportRows(
          reportRes.data
            .map((r) => ({
              name: r.menteeName,
              category: r.objectiveType || '',
              description: r.description || '',
              status: r.status || '',
            }))
            .filter((r) => r.name)
        );
      }
      if (quartersRes.error) return myRef.current?.call('error', quartersRes.error?.message);
      setQuarters(quartersRes.data);
      const active = quartersRes.data.find((c) => c.status === 1) || quartersRes.data[0];
      if (active) {
        setSelectedYear(String(dayjs(active.startDate).year()));
        setSelectedId(ALL_QUARTERS); // default to all quarters of the active year
      } else setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!selectedId || quarters.length === 0) return;
    (async () => {
      setLoading(true);
      if (selectedId === ALL_QUARTERS) {
        const qs = quarters
          .filter((c) => quarterYear(c) === selectedYear)
          .sort((a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf());
        const results = await Promise.all(qs.map((c) => getQuarterlyUsers(c.id, 'hr')));
        setLoading(false);
        const failed = results.find((r) => r.error);
        if (failed?.error) return myRef.current?.call('error', failed.error?.message);
        setEmployees(aggregateQuarters(qs, results));
      } else {
        const { data, error } = await getQuarterlyUsers(selectedId, 'hr');
        setLoading(false);
        if (error) return myRef.current?.call('error', error?.message);
        setEmployees((data || []).map(toEmployee));
      }
      clearAll();
    })();
  }, [selectedId, selectedYear]);

  // build the submission trend (last 6 quarters) the first time the Feedback view opens
  useEffect(() => {
    if (!SHOW_TREND || view !== 'feedback' || quarters.length === 0 || trendBuilt.current) return;
    trendBuilt.current = true;
    (async () => {
      setTrendLoading(true);
      const recent = [...quarters]
        .sort((a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf())
        .slice(-6);
      const results = await Promise.all(recent.map((qr) => getQuarterlyUsers(qr.id, 'hr')));
      const points: TrendPoint[] = recent.map((qr, i) => {
        const emps = (results[i].data || []).map(toEmployee);
        const met = computeMetrics(emps);
        return {
          id: qr.id,
          name: qr.name,
          mentorPct: met.mentorsPct,
          menteePct: met.menteesPct,
          total: met.total,
        };
      });
      setTrend(points);
      setTrendLoading(false);
    })();
  }, [view, quarters]);

  // name -> unique objective categories (from the objectives report)
  const empCatMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    reportRows.forEach((r) => {
      if (!r.category) return;
      if (!map[r.name]) map[r.name] = [];
      if (!map[r.name].includes(r.category)) map[r.name].push(r.category);
    });
    return map;
  }, [reportRows]);

  // name -> full list of objectives (for the per-row "view objectives" modal)
  const empObjMap = useMemo(() => {
    const map: Record<string, { description: string; category: string; status: string }[]> = {};
    reportRows.forEach((r) => {
      if (!map[r.name]) map[r.name] = [];
      map[r.name].push({ description: r.description, category: r.category, status: r.status });
    });
    return map;
  }, [reportRows]);

  const mentors = Array.from(
    new Set(employees.map((e) => (hasMentor(e.mentor) ? e.mentor : 'Unassigned')))
  ).sort();
  const tiers = Array.from(new Set(employees.map((e) => String(e.tier)))).sort(
    (a, b) => Number(a) - Number(b)
  );
  const designations = Array.from(
    new Set(employees.map((e) => e.designation).filter(Boolean))
  ).sort();

  // base filters apply to the chart AND the table so they always agree
  const toggleTier = (t: string) =>
    setTierSel((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  const toggleDesig = (d: string) =>
    setDesigSel((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]));
  const toggleMentor = (mn: string) =>
    setMentorSel((cur) => (cur.includes(mn) ? cur.filter((x) => x !== mn) : [...cur, mn]));

  const matchesBase = (e: Employee) => {
    if (tierSel.length > 0 && !tierSel.includes(String(e.tier))) return false;
    if (desigSel.length > 0 && !desigSel.includes(e.designation)) return false;
    const mentorKey = hasMentor(e.mentor) ? e.mentor : 'Unassigned';
    if (mentorSel.length > 0 && !mentorSel.includes(mentorKey)) return false;
    if (q && !`${e.name} ${e.designation}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  };

  const baseRows = useMemo(
    () => employees.filter(matchesBase),
    [employees, tierSel, desigSel, mentorSel, q]
  );
  const m = useMemo(() => computeMetrics(baseRows), [baseRows]);

  /* ---------- Objectives view: how many EMPLOYEES have each category ---------- */
  const catCounts = useMemo(() => {
    const c: Record<string, number> = {};
    baseRows.forEach((e) => {
      (empCatMap[e.name] || []).forEach((cat) => {
        c[cat] = (c[cat] || 0) + 1;
      });
    });
    return c;
  }, [empCatMap, baseRows]);

  const catKeys = [
    ...CATEGORIES.filter((c) => catCounts[c]),
    ...Object.keys(catCounts).filter((c) => !CATEGORIES.includes(c)),
  ];
  const usingCategories = catKeys.length > 0;

  /* ---------- total OBJECTIVES set + per-category objective counts (exec summary) ---------- */
  const objCatCounts = useMemo(() => {
    const names = new Set(baseRows.map((e) => e.name));
    const c: Record<string, number> = {};
    reportRows.forEach((r) => {
      if (!names.has(r.name)) return;
      const key = r.category || 'Uncategorized';
      c[key] = (c[key] || 0) + 1;
    });
    return c;
  }, [reportRows, baseRows]);
  const objCatKeys = [
    ...CATEGORIES.filter((c) => objCatCounts[c]),
    ...Object.keys(objCatCounts).filter((c) => !CATEGORIES.includes(c)),
  ];
  const totalObjectivesSet = Object.values(objCatCounts).reduce((a, b) => a + b, 0);

  const pieSlices: PieSliceData[] = usingCategories
    ? catKeys.map((c) => ({
        name: c,
        // "Has" mode → employees who have the category; "Doesn't have" mode → employees who don't
        value: catMode === 'has' ? catCounts[c] : m.total - catCounts[c],
        color: catStyle(c).color,
        dark: catStyle(c).dark,
      }))
    : [
        { name: 'Has objectives', value: m.haveObjectives, color: C.green, dark: C.greenDark },
        { name: 'No objectives yet', value: m.noObjectives, color: C.amber, dark: C.amberText },
      ].filter((s) => s.value > 0);

  // the currently selected pie-chart categories (empty when "has"/"none"/nothing is selected)
  const activeCats = objFilter.filter((f) => f !== 'has' && f !== 'none');
  const noneSel = objFilter.includes('none');
  const hasSel = objFilter.includes('has');

  const pieActive = usingCategories
    ? activeCats
    : [...(hasSel ? ['Has objectives'] : []), ...(noneSel ? ['No objectives yet'] : [])];

  const toggleCat = (name: string) =>
    setObjFilter((cur) => {
      const cats = cur.filter((f) => f !== 'has' && f !== 'none'); // selecting a category drops has/none
      return cats.includes(name) ? cats.filter((c) => c !== name) : [...cats, name];
    });

  const onPieSlice = (name: string) => {
    if (!usingCategories) {
      const key = name === 'Has objectives' ? 'has' : 'none';
      setObjFilter((cur) => (cur.includes(key) ? [] : [key]));
    } else {
      toggleCat(name);
    }
  };

  // category-scoped columns/exports only make sense in "has" mode
  const scopeCats = catMode === 'has' ? activeCats : [];

  const objRows = baseRows.filter((e) => {
    if (objFilter.length === 0) return true;
    if (hasSel) return e.objectives > 0;
    if (noneSel) return e.objectives === 0;
    const cats = empCatMap[e.name] || [];
    return catMode === 'has'
      ? activeCats.some((c) => cats.includes(c)) // has ANY of the selected categories
      : activeCats.every((c) => !cats.includes(c)); // has NONE of the selected categories
  });

  // objectives in scope of the current category selection (all when nothing selected / "without" mode)
  const scopedObjectives = (name: string) => {
    const all = empObjMap[name] || [];
    return scopeCats.length > 0 ? all.filter((o) => scopeCats.includes(o.category)) : all;
  };

  const openObjModal = (e: Employee) =>
    setObjModal({ name: e.name, objectives: scopedObjectives(e.name) });

  // coverage by tier (org-wide overview; click a row to drill in by tier)
  const tierCoverage = useMemo(() => {
    const map: Record<string, { have: number; total: number }> = {};
    employees.forEach((e) => {
      const t = String(e.tier);
      if (!map[t]) map[t] = { have: 0, total: 0 };
      map[t].total++;
      if (e.objectives > 0) map[t].have++;
    });
    return Object.entries(map)
      .map(([tier, v]) => ({ tier, have: v.have, total: v.total, pct: round(v.have, v.total) }))
      .sort((a, b) => Number(a.tier) - Number(b.tier));
  }, [employees]);

  /* ---------- Feedback view ---------- */
  const toggleFb = (role: 'mentor' | 'mentee', status: Band) =>
    setFbFilter((f) => {
      const idx = f.findIndex((item) => item.role === role && item.status === status);
      if (idx !== -1) return f.filter((_, i) => i !== idx);
      return [...f, { role, status }];
    });

  // lateness is measured against the SELECTED quarter's deadlines. In "All quarters"
  // mode there is no single deadline, so we use the per-quarter flag computed at merge time.
  const allQuarters = selectedId === ALL_QUARTERS;
  const mentorLate = (e: Employee) =>
    allQuarters ? !!e.mentorLateAny : isLateSubmission(e.mentorSubmissionDate, meta?.mentorDeadline);
  const menteeLate = (e: Employee) =>
    allQuarters ? !!e.menteeLateAny : isLateSubmission(e.menteeSubmissionDate, meta?.menteeDeadline);
  const isEmpLate = (e: Employee) => mentorLate(e) || menteeLate(e);

  const lateCount = useMemo(
    () => baseRows.filter(isEmpLate).length,
    [baseRows, meta?.mentorDeadline, meta?.menteeDeadline, allQuarters]
  );

  const fbRows = baseRows.filter((e) => {
    if (lateSel && !isEmpLate(e)) return false;
    if (fbFilter.length === 0) return true;
    const mentorBand: Band = hasMentor(e.mentor) ? e.mentorStatus : 'No mentor';
    const menteeBand: Band = e.menteeStatus;
    const mSel = fbFilter.filter((f) => f.role === 'mentor');
    const eSel = fbFilter.filter((f) => f.role === 'mentee');
    if (mSel.length > 0 && !mSel.some((f) => f.status === mentorBand)) return false;
    if (eSel.length > 0 && !eSel.some((f) => f.status === menteeBand)) return false;
    return true;
  });

  const sortVal = (e: Employee, key: SortKey): string | number => {
    switch (key) {
      case 'name':
        return e.name.toLowerCase();
      case 'designation':
        return (e.designation || '').toLowerCase();
      case 'tier':
        return Number(e.tier);
      case 'mentor':
        return hasMentor(e.mentor) ? e.mentor.toLowerCase() : 'zzzz'; // unassigned sorts last
      case 'categories':
        return (empCatMap[e.name] || []).length;
      case 'objectives':
        return e.objectives;
      case 'mentorFb':
        return BAND_ORDER.indexOf(hasMentor(e.mentor) ? e.mentorStatus : 'No mentor');
      case 'menteeFb':
        return BAND_ORDER.indexOf(e.menteeStatus);
    }
  };

  const viewRows = view === 'objectives' ? objRows : fbRows;
  const rows = sort
    ? [...viewRows].sort((a, b) => {
        const va = sortVal(a, sort.key);
        const vb = sortVal(b, sort.key);
        let cmp =
          typeof va === 'number' && typeof vb === 'number'
            ? va - vb
            : String(va).localeCompare(String(vb));
        if (cmp === 0) cmp = a.name.localeCompare(b.name); // stable tiebreak by name
        return sort.dir === 'asc' ? cmp : -cmp;
      })
    : viewRows; // default: natural order from the API (by name)

  const anyFilter =
    tierSel.length > 0 ||
    desigSel.length > 0 ||
    mentorSel.length > 0 ||
    !!q ||
    (view === 'objectives' ? objFilter.length > 0 : fbFilter.length > 0 || lateSel);

  function clearAll() {
    setTierSel([]);
    setDesigSel([]);
    setMentorSel([]);
    setQ('');
    setObjFilter([]);
    setCatMode('has');
    setFbFilter([]);
    setLateSel(false);
    setHotSlice(null);
    setSelectMode(false);
    setSelectedNames([]);
    setSort(null);
  }

  const exportExcel = () => {
    const cycle = meta?.name || 'cycle';
    // in select mode only the checked employees are exported
    const exportRows =
      selectMode && selectedNames.length > 0
        ? rows.filter((e) => selectedNames.includes(e.name))
        : rows;
    let data: Record<string, any>[];
    let sheetName: string;

    if (view === 'objectives' && noneSel) {
      // people with no objectives yet — nothing to list per-objective, so export the roster
      data = exportRows.map((e) => ({
        Individual: e.name,
        Designation: e.designation,
        Tier: e.tier,
        'Mentor Name': e.mentor,
      }));
      sheetName = 'No Objectives';
    } else if (view === 'objectives' && usingCategories) {
      // one row per objective, with its category in a dedicated column
      const exportCats = scopeCats.length > 0 ? scopeCats : catKeys;
      data = exportRows.flatMap((e) => {
        const base = {
          Individual: e.name,
          Designation: e.designation,
          Tier: e.tier,
          'Mentor Name': e.mentor,
        };
        const objs = exportCats.flatMap((cat) =>
          (empObjMap[e.name] || []).filter((o) => o.category === cat)
        );
        if (objs.length === 0)
          return [{ ...base, 'Objective Category': '—', Objective: '—', Status: '—' }];
        return objs.map((o) => ({
          ...base,
          'Objective Category': o.category,
          Objective: o.description,
          Status: prettyStatus(o.status),
        }));
      });
      sheetName = scopeCats.length === 1 ? scopeCats[0] : 'Objectives';
    } else if (view === 'objectives') {
      data = exportRows.map((e) => ({
        Individual: e.name,
        Designation: e.designation,
        Tier: e.tier,
        'Mentor Name': e.mentor,
        'Objectives Set': e.objectives,
      }));
      sheetName = 'Objectives';
    } else {
      const fmt = (d?: string | null) => (d ? dayjs(d).format('MMM DD YYYY') : '—');
      data = exportRows.map((e) => {
        const mLate = mentorLate(e);
        const eLate = menteeLate(e);
        return {
          Mentee: e.name,
          Designation: e.designation,
          Tier: e.tier,
          'Mentor Name': e.mentor,
          // mirror the UI: a late role reads "Late submission" instead of its completion status
          'Mentor Feedback': mLate ? 'Late submission' : hasMentor(e.mentor) ? e.mentorStatus : 'No mentor',
          'Mentee Feedback': eLate ? 'Late submission' : e.menteeStatus,
          'Mentor Submitted': fmt(e.mentorSubmissionDate),
          'Mentee Submitted': fmt(e.menteeSubmissionDate),
          'Mentor Deadline': allQuarters ? 'Varies (all quarters)' : fmt(meta?.mentorDeadline),
          'Mentee Deadline': allQuarters ? 'Varies (all quarters)' : fmt(meta?.menteeDeadline),
          'Late Submission': mLate && eLate ? 'Mentor & Mentee' : mLate ? 'Mentor' : eLate ? 'Mentee' : 'No',
        };
      });
      sheetName = lateSel ? 'Late Submissions' : 'Feedback';
    }

    const ws = XLSX.utils.json_to_sheet(data);
    // size each column to its content (capped) so objective lists stay readable
    if (data.length > 0) {
      ws['!cols'] = Object.keys(data[0]).map((key) => ({
        wch: Math.min(
          80,
          Math.max(key.length, ...data.map((r) => String(r[key] ?? '').length))
        ),
      }));
    }
    // Feedback export: paint late-submission rows red (Excel's "bad" style)
    if (view === 'feedback' && data.length > 0) {
      const colCount = Object.keys(data[0]).length;
      data.forEach((row, i) => {
        if (row['Late Submission'] && row['Late Submission'] !== 'No') {
          for (let c = 0; c < colCount; c++) {
            const ref = XLSX.utils.encode_cell({ r: i + 1, c }); // +1: row 0 is the header
            if (!ws[ref]) continue;
            ws[ref].s = {
              fill: { patternType: 'solid', fgColor: { rgb: 'FFC7CE' } },
              font: { color: { rgb: '9C0006' }, bold: true },
            };
          }
        }
      });
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([buffer], { type: 'application/octet-stream' }),
      `SparkTalent_${cycle.replace(/\s+/g, '_')}_${sheetName.replace(/\s+/g, '_')}.xlsx`
    );
  };

  const sectionLabel = (text: string) => (
    <span
      className="text-[12px] font-semibold uppercase"
      style={{ color: C.gray, letterSpacing: '.1em' }}
    >
      {text}
    </span>
  );

  const filterChip = (f: string) =>
    f === 'has'
      ? { bg: C.greenSoft, text: C.green, label: 'Has objectives' }
      : f === 'none'
        ? { bg: C.amberSoft, text: C.amberText, label: 'No objectives yet' }
        : {
            bg: catStyle(f).soft,
            text: catStyle(f).text,
            label: catMode === 'without' ? `Without: ${f}` : f,
          };

  // sortable table header cell (asc → desc → default)
  const sortTh = (label: string, key: SortKey, extra = '') => {
    const on = sort?.key === key;
    return (
      <th
        className={`cursor-pointer select-none px-4 py-2.5 font-semibold transition-colors hover:text-[#191F1B] ${extra}`}
        onClick={() => toggleSort(key)}
        title={
          on
            ? sort?.dir === 'asc'
              ? 'Sorted ascending — click for descending'
              : 'Sorted descending — click to reset'
            : 'Sort by this column'
        }
      >
        <span className="inline-flex items-center gap-1">
          {label}
          <span style={{ fontSize: 9, opacity: on ? 0.9 : 0.3 }}>
            {on ? (sort?.dir === 'asc' ? '▲' : '▼') : '⇅'}
          </span>
        </span>
      </th>
    );
  };

  return (
    <div
      className="flex flex-col gap-y-[10px] rounded-2xl border p-6"
      style={{ background: C.paper, color: C.ink, borderColor: C.line }}
    >
      {/* header + toggle */}
      <div className="mb-1 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[26px] font-bold" style={{ letterSpacing: '-.02em' }}>
            {view === 'objectives' ? 'Objectives Dashboard' : 'Feedback Dashboard'}
          </div>
          <div className="text-[13.5px]" style={{ color: C.gray }}>
            {view === 'objectives'
              ? 'What objectives people are working on — by category — and who has none.'
              : 'Who has submitted their quarterly feedback — and who is behind.'}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="inline-flex rounded-full p-1" style={{ background: C.soft }}>
            {(['objectives', 'feedback'] as View[]).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className="rounded-full px-5 py-1.5 text-[13px] font-semibold capitalize transition-colors"
                style={
                  view === v
                    ? { background: C.ink, color: C.paper, border: 'none', cursor: 'pointer' }
                    : { background: 'transparent', color: C.gray2, border: 'none', cursor: 'pointer' }
                }
              >
                {v}
              </button>
            ))}
          </div>
          {!loading && employees.length > 0 && (
            <div
              className="flex items-center gap-3 rounded-2xl border px-4 py-2.5"
              style={{ background: C.card, borderColor: C.line }}
            >
              <span
                className="flex h-11 w-11 items-center justify-center rounded-full"
                style={{ background: C.green, color: '#fff' }}
              >
                <TeamOutlined style={{ fontSize: 20 }} />
              </span>
              <div className="leading-none">
                <div className="flex items-baseline gap-1.5">
                  <span
                    className="text-[34px] font-bold"
                    style={{ color: C.ink, letterSpacing: '-.03em' }}
                  >
                    {employees.length}
                  </span>
                  <span className="text-[13px] font-semibold" style={{ color: C.gray2 }}>
                    headcount
                  </span>
                </div>
                <div
                  className="mt-1 text-[10.5px] font-semibold uppercase"
                  style={{ color: C.grayFaint, letterSpacing: '.12em' }}
                >
                  {selectedId === ALL_QUARTERS ? `All quarters · ${selectedYear}` : meta?.name || 'this quarter'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* cycle selector */}
      <div className="flex flex-wrap items-center gap-3">
        <Dropdown
          value={selectedYear}
          onChange={handleYearChange}
          options={years}
          label={(y) => y || 'Select year'}
        />
        <Dropdown
          value={selectedId}
          onChange={setSelectedId}
          options={[ALL_QUARTERS, ...yearQuarters.map((c) => c.id)]}
          label={(id) =>
            id === ALL_QUARTERS
              ? 'All quarters'
              : quarters.find((c) => c.id === id)?.name || 'Select cycle'
          }
        />
        {selectedId === ALL_QUARTERS ? (
          <span className="flex items-center gap-3 text-[12.5px]" style={{ color: C.gray2 }}>
            <span className="font-semibold" style={{ color: C.ink }}>
              All quarters · {selectedYear}
            </span>
            <span className="inline-block h-1 w-1 rounded-full" style={{ background: C.greyBand }} />
            {yearQuarters.length} {yearQuarters.length === 1 ? 'quarter' : 'quarters'} combined
          </span>
        ) : (
          meta && (
            <span className="flex items-center gap-3 text-[12.5px]" style={{ color: C.gray2 }}>
              <span className="font-semibold" style={{ color: C.ink }}>
                {meta.name}
              </span>
              <span className="inline-block h-1 w-1 rounded-full" style={{ background: C.greyBand }} />
              {dayjs(meta.startDate).format('MMM DD YYYY')} – {dayjs(meta.endDate).format('MMM DD YYYY')}
            </span>
          )
        )}
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm" style={{ color: C.gray }}>
          Loading…
        </div>
      ) : (
        <>
          {/* ============ OBJECTIVES VIEW ============ */}
          {view === 'objectives' && (
            <div className="border-b pb-8 pt-3" style={{ borderColor: C.line }}>
              <div className="mb-3">
                {sectionLabel(
                  usingCategories
                    ? catMode === 'without'
                      ? 'Objectives by category — click a slice to exclude that category'
                      : 'Objectives by category — click a slice to filter'
                    : 'Objective coverage — click a slice to filter'
                )}
              </div>
              <div className="flex flex-col items-center gap-8 lg:flex-row lg:flex-wrap lg:items-center lg:justify-center lg:gap-x-12 lg:gap-y-8">
                {/* objective coverage — one tile: total objectives, category breakdown, with/without groups */}
                <div
                  className="w-full shrink-0 rounded-2xl border p-5 lg:w-[280px]"
                  style={{ borderColor: C.line, background: C.card }}
                >
                  <div
                    className="text-[11px] font-semibold uppercase"
                    style={{ color: C.grayFaint, letterSpacing: '.1em' }}
                  >
                    Objectives set
                  </div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-[42px] font-bold leading-none" style={{ letterSpacing: '-.03em' }}>
                      {totalObjectivesSet}
                    </span>
                    <span className="text-[12.5px] font-semibold" style={{ color: C.gray2 }}>
                      objectives
                    </span>
                  </div>
                  <div
                    className="mt-1 text-[12px] font-semibold uppercase"
                    style={{ color: C.gray, letterSpacing: '.09em' }}
                  >
                    across the organization
                  </div>

                  {/* objectives broken down by category */}
                  {objCatKeys.length > 0 && (
                    <div className="mt-4 flex flex-col gap-1.5">
                      {objCatKeys.map((c) => {
                        const st = catStyle(c);
                        return (
                          <div key={c} className="flex items-center gap-2">
                            <span
                              className="h-[9px] w-[9px] shrink-0 rounded-full"
                              style={{ background: st.color }}
                            />
                            <span className="flex-1 text-[12.5px] font-medium" style={{ color: C.gray2 }}>
                              {c}
                            </span>
                            <span className="text-[13.5px] font-bold" style={{ color: C.ink }}>
                              {objCatCounts[c]}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="my-4 border-t" style={{ borderColor: C.line }} />

                  {/* split bar: with objectives vs without (2px gap between fills) */}
                  <div className="flex h-[10px] w-full gap-[2px] overflow-hidden rounded-full">
                    <span
                      className="h-full rounded-full"
                      style={{
                        width: `${(m.haveObjectives / (m.total || 1)) * 100}%`,
                        background: C.green,
                        opacity: noneSel ? 0.35 : 1,
                        transition: 'opacity .2s',
                      }}
                    />
                    <span
                      className="h-full flex-1 rounded-full"
                      style={{
                        background: C.amber,
                        opacity: hasSel ? 0.35 : 1,
                        transition: 'opacity .2s',
                      }}
                    />
                  </div>

                  {/* the two groups, side by side, clickable as filters */}
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => {
                        setObjFilter((cur) => (cur.includes('has') ? [] : ['has']));
                        scrollToTable();
                      }}
                      className="flex-1 rounded-xl px-3 py-2.5 text-left transition-colors"
                      style={{
                        background: hasSel ? C.greenSoft : C.soft,
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <span className="flex items-center gap-1.5">
                        <span className="h-[9px] w-[9px] shrink-0 rounded-full" style={{ background: C.green }} />
                        <span className="text-[20px] font-bold leading-none" style={{ color: C.ink }}>
                          {m.haveObjectives}
                        </span>
                      </span>
                      <span
                        className="mt-1 block text-[10.5px] font-semibold uppercase"
                        style={{ color: C.gray2, letterSpacing: '.09em' }}
                      >
                        Individuals with objectives
                      </span>
                    </button>
                    <button
                      onClick={() => {
                        setObjFilter((cur) => (cur.includes('none') ? [] : ['none']));
                        scrollToTable();
                      }}
                      className="flex-1 rounded-xl px-3 py-2.5 text-left transition-colors"
                      style={{
                        background: noneSel ? C.amberSoft : C.soft,
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <span className="flex items-center justify-between gap-1.5">
                        <span className="flex items-center gap-1.5">
                          <span className="h-[9px] w-[9px] shrink-0 rounded-full" style={{ background: C.amber }} />
                          <span className="text-[20px] font-bold leading-none" style={{ color: C.ink }}>
                            {m.noObjectives}
                          </span>
                        </span>
                        <span className="font-semibold" style={{ color: C.amberText }}>
                          {noneSel ? '↓' : '→'}
                        </span>
                      </span>
                      <span
                        className="mt-1 block text-[10.5px] font-semibold uppercase"
                        style={{ color: C.gray2, letterSpacing: '.09em' }}
                      >
                        Individuals without objectives
                      </span>
                    </button>
                  </div>
                </div>

                <Pie3D
                  slices={pieSlices}
                  unit="individuals"
                  hot={hotSlice}
                  active={pieActive}
                  onHot={setHotSlice}
                  onClick={onPieSlice}
                  onBackgroundClick={() => setObjFilter([])}
                />

                {/* category legend (right of the pie) */}
                <div className="w-full lg:w-auto lg:border-l lg:pl-10" style={{ borderColor: C.line }}>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <span
                      className="text-[11px] font-semibold uppercase"
                      style={{ color: C.grayFaint, letterSpacing: '.1em' }}
                    >
                      {usingCategories ? 'Individuals per category' : 'Breakdown'}
                    </span>
                    {usingCategories && (
                      <div className="inline-flex rounded-full p-0.5" style={{ background: C.soft }}>
                        {(['has', 'without'] as const).map((mode) => (
                          <button
                            key={mode}
                            onClick={() => setCatMode(mode)}
                            title={
                              mode === 'has'
                                ? 'Show people who HAVE the selected category'
                                : "Show people who do NOT have the selected category"
                            }
                            className="rounded-full px-2.5 py-1 text-[10.5px] font-semibold transition-colors"
                            style={
                              catMode === mode
                                ? { background: C.ink, color: C.paper, border: 'none', cursor: 'pointer' }
                                : { background: 'transparent', color: C.gray2, border: 'none', cursor: 'pointer' }
                            }
                          >
                            {mode === 'has' ? 'Has' : "Doesn't have"}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="-mx-2 flex flex-col gap-0.5">
                    {usingCategories ? (
                      catKeys.map((c) => {
                        const st = catStyle(c);
                        const cnt = catMode === 'has' ? catCounts[c] : m.total - catCounts[c];
                        return (
                          <button
                            key={c}
                            onClick={() => toggleCat(c)}
                            onMouseEnter={() => setHotSlice(c)}
                            onMouseLeave={() => setHotSlice(null)}
                            className="flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors"
                            style={{
                              background: activeCats.includes(c) ? C.soft : 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                            }}
                          >
                            <span
                              className="h-[11px] w-[11px] shrink-0 rounded"
                              style={{
                                background: catMode === 'has' ? st.color : 'transparent',
                                border: catMode === 'has' ? 'none' : `2px solid ${st.color}`,
                              }}
                            />
                            <span className="w-[150px] text-[13.5px] font-semibold" style={{ color: C.ink }}>
                              {c}
                            </span>
                            <span className="w-20 text-right text-[12.5px]" style={{ color: C.gray2 }}>
                              <span className="text-sm font-bold" style={{ color: C.ink }}>
                                {cnt}
                              </span>{' '}
                              {catMode === 'has' ? (cnt === 1 ? 'has' : 'have') : 'without'}
                            </span>
                          </button>
                        );
                      })
                    ) : (
                      <p className="px-2.5 text-xs" style={{ color: C.gray }}>
                        Category data isn't available right now.
                      </p>
                    )}
                  </div>
                </div>

                {/* coverage by tier (fills the right; click a tier to filter) */}
                {tierCoverage.length > 0 && (
                  <div className="w-full lg:w-auto lg:border-l lg:pl-10" style={{ borderColor: C.line }}>
                    <div
                      className="mb-2 text-[11px] font-semibold uppercase"
                      style={{ color: C.grayFaint, letterSpacing: '.1em' }}
                    >
                      Coverage by tier
                    </div>
                    <div className="-mx-2 flex flex-col gap-0.5">
                      {tierCoverage.map((t) => (
                        <button
                          key={t.tier}
                          onClick={() => toggleTier(t.tier)}
                          title={`Tier ${t.tier} · ${t.have} of ${t.total} have objectives · click to filter`}
                          className="flex items-center gap-2.5 rounded-xl px-2.5 py-1.5 text-left transition-colors"
                          style={{
                            background: tierSel.includes(t.tier) ? C.soft : 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          <span className="w-7 shrink-0 text-[12.5px] font-bold" style={{ color: C.ink }}>
                            T{t.tier}
                          </span>
                          <span
                            className="block h-2 w-24 shrink-0 overflow-hidden rounded-full"
                            style={{ background: C.soft }}
                          >
                            <span
                              className="block h-full rounded-full"
                              style={{ width: `${t.pct}%`, background: t.pct >= 50 ? C.green : C.amber }}
                            />
                          </span>
                          <span className="w-24 text-right text-[12.5px]" style={{ color: C.gray2 }}>
                            <span className="text-sm font-bold" style={{ color: C.ink }}>
                              {t.have}
                            </span>{' '}
                            of {t.total}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ============ FEEDBACK VIEW ============ */}
          {view === 'feedback' && (
            <div className="border-b pb-8 pt-3" style={{ borderColor: C.line }}>
              <div className="mb-4 flex flex-wrap items-center gap-x-8 gap-y-2">
                {sectionLabel('Submission status — click any band to filter below')}
                <div className="flex flex-wrap gap-4 text-xs" style={{ color: C.gray2 }}>
                  {BAND_ORDER.map((s) => (
                    <span key={s} className="inline-flex items-center gap-1.5">
                      <span className="h-[9px] w-[9px] rounded" style={{ background: BAND[s].full }} />
                      {s}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch lg:gap-6">
                {/* status bars take most of the width */}
                <div
                  className="flex flex-1 flex-col justify-center gap-2 lg:border-r-2 lg:pr-8"
                  style={{ borderColor: C.grayFaint }}
                >
                  <StatusBar
                    role="mentor"
                    label="Mentor feedback"
                    bands={m.mentorBands}
                    filter={fbFilter}
                    onToggle={toggleFb}
                  />
                  <StatusBar
                    role="mentee"
                    label="Mentee feedback"
                    bands={m.menteeBands}
                    filter={fbFilter}
                    onToggle={toggleFb}
                  />
                </div>

                {/* late submissions — narrower card; click to filter the table below */}
                <button
                  onClick={() => {
                    if (lateCount === 0) return;
                    setLateSel((v) => !v);
                    scrollToTable();
                  }}
                  disabled={lateCount === 0}
                  className="flex w-full shrink-0 flex-col justify-center gap-1.5 rounded-2xl border p-4 text-left transition-colors disabled:cursor-default lg:w-[210px]"
                  style={{
                    borderColor: lateSel ? C.red : C.line,
                    background: lateSel ? C.redSoft : C.card,
                    cursor: lateCount === 0 ? 'default' : 'pointer',
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                      style={{ background: lateCount > 0 ? C.red : C.greyBand, color: '#fff' }}
                    >
                      <ClockCircleOutlined style={{ fontSize: 18 }} />
                    </span>
                    <span
                      className="text-[32px] font-bold leading-none"
                      style={{ color: lateCount > 0 ? C.red : C.gray2, letterSpacing: '-.03em' }}
                    >
                      {lateCount}
                    </span>
                  </div>
                  <div
                    className="text-[11px] font-semibold uppercase"
                    style={{ color: C.gray, letterSpacing: '.09em' }}
                  >
                    Late {lateCount === 1 ? 'submission' : 'submissions'}
                  </div>
                  <div className="text-[11.5px] leading-snug" style={{ color: C.gray }}>
                    {lateCount > 0
                      ? 'Submitted after the deadline (mentor or mentee).'
                      : 'All on-time — nobody submitted past the deadline.'}
                  </div>
                  {lateCount > 0 && (
                    <span className="text-[12px] font-semibold" style={{ color: C.red }}>
                      {lateSel ? 'Showing below ↓' : 'Click to see who →'}
                    </span>
                  )}
                </button>
              </div>

              {/* submission trend across recent quarters (hidden for now via SHOW_TREND) */}
              {SHOW_TREND && (
              <div className="mt-8 border-t pt-6" style={{ borderColor: C.line }}>
                <div className="mb-3">
                  {sectionLabel('Submission trend — completion % across recent quarters')}
                </div>
                {trendLoading ? (
                  <div className="py-10 text-center text-sm" style={{ color: C.gray }}>
                    Loading trend…
                  </div>
                ) : trend.length >= 2 ? (
                  <TrendChart data={trend} />
                ) : (
                  <div className="py-8 text-center text-sm" style={{ color: C.gray }}>
                    Not enough history yet to show a trend.
                  </div>
                )}
              </div>
              )}
            </div>
          )}

          {/* ============ WHO EXACTLY (table, shared) ============ */}
          <div ref={tableRef} className="mt-4 pt-2 scroll-mt-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="text-[17px] font-bold" style={{ color: C.ink, letterSpacing: '-.01em' }}>
                  Who exactly?
                </h3>
                <span className="text-[12.5px]" style={{ color: C.gray }}>
                  {rows.length} of {m.total} people
                </span>
                {view === 'objectives' &&
                  objFilter.map((f) => {
                    const chip = filterChip(f);
                    return (
                      <Pill key={f} bg={chip.bg} text={chip.text}>
                        <span className="inline-flex items-center gap-1.5">
                          {chip.label}
                          <button
                            onClick={() => setObjFilter((cur) => cur.filter((x) => x !== f))}
                          >
                            <CloseOutlined style={{ fontSize: 10 }} />
                          </button>
                        </span>
                      </Pill>
                    );
                  })}
                {view === 'feedback' &&
                  fbFilter.map((f, i) => (
                    <Pill key={i} bg={BAND[f.status].pillBg} text={BAND[f.status].pillText}>
                      <span className="inline-flex items-center gap-1.5">
                        {f.role === 'mentor' ? 'Mentor' : 'Mentee'}: {f.status}
                        <button onClick={() => setFbFilter((prev) => prev.filter((_, j) => j !== i))}>
                          <CloseOutlined style={{ fontSize: 10 }} />
                        </button>
                      </span>
                    </Pill>
                  ))}
                {view === 'feedback' && lateSel && (
                  <Pill bg={C.redSoft} text={C.red}>
                    <span className="inline-flex items-center gap-1.5">
                      Late submissions
                      <button onClick={() => setLateSel(false)}>
                        <CloseOutlined style={{ fontSize: 10 }} />
                      </button>
                    </span>
                  </Pill>
                )}
                {anyFilter && (
                  <button
                    onClick={clearAll}
                    className="text-xs font-semibold underline"
                    style={{ color: C.gray }}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2.5">
                <MultiDropdown
                  selected={tierSel}
                  onToggle={toggleTier}
                  onClear={() => setTierSel([])}
                  options={tiers}
                  allLabel="All tiers"
                  itemLabel={(v) => `Tier ${v}`}
                />
                <MultiDropdown
                  selected={desigSel}
                  onToggle={toggleDesig}
                  onClear={() => setDesigSel([])}
                  options={designations}
                  allLabel="All designations"
                  itemLabel={(v) => v}
                />
                <MultiDropdown
                  selected={mentorSel}
                  onToggle={toggleMentor}
                  onClear={() => setMentorSel([])}
                  options={mentors}
                  allLabel="All mentors"
                  itemLabel={(v) => v}
                />
                <div className="relative">
                  <SearchOutlined style={{ color: C.gray }} className="absolute left-3.5 top-2.5" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search name…"
                    className="rounded-full border py-2 pl-9 pr-4 text-[13px] outline-none focus:border-[#191F1B]"
                    style={{ borderColor: C.line, background: C.card, color: C.ink, width: 180 }}
                  />
                </div>
                <button
                  onClick={toggleSelectMode}
                  disabled={rows.length === 0}
                  title={
                    selectMode
                      ? 'Exit select mode and clear the selection'
                      : 'Pick specific employees to export'
                  }
                  className="rounded-full border px-4 py-2 text-[12.5px] font-semibold transition-colors disabled:opacity-50"
                  style={
                    selectMode
                      ? { background: C.soft, color: C.ink, borderColor: C.ink, cursor: 'pointer' }
                      : { background: C.card, color: C.ink, borderColor: C.line, cursor: 'pointer' }
                  }
                >
                  {selectMode ? 'Cancel' : 'Select'}
                </button>
                <button
                  onClick={exportExcel}
                  disabled={rows.length === 0 || (selectMode && selectedNames.length === 0)}
                  title={
                    selectMode
                      ? selectedNames.length > 0
                        ? `Export the ${selectedNames.length} selected employee(s) to Excel`
                        : 'Tick at least one employee to export'
                      : 'Export the people shown below (current filters) to Excel'
                  }
                  className="inline-flex items-center gap-1.5 rounded-full border-none px-4 py-2 text-[12.5px] font-semibold transition-colors disabled:opacity-50"
                  style={{ background: C.ink, color: C.paper, cursor: 'pointer' }}
                >
                  <DownloadOutlined /> Export
                  {selectMode && selectedNames.length > 0 ? ` (${selectedNames.length})` : ''}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-[1]" style={{ background: C.paper }}>
                    <tr
                      className="text-left text-[11px] uppercase"
                      style={{ color: C.grayFaint, letterSpacing: '.08em' }}
                    >
                      {selectMode && <th className="w-10 px-4 py-2.5" aria-label="Select" />}
                      {sortTh(view === 'objectives' ? 'Individual' : 'Mentee', 'name')}
                      {sortTh('Designation', 'designation')}
                      {sortTh('Tier', 'tier')}
                      {sortTh('Mentor', 'mentor')}
                      {view === 'objectives' ? (
                        <>
                          {sortTh(
                            scopeCats.length > 0 ? 'Selected Categories' : 'Objective Categories',
                            'categories'
                          )}
                          {sortTh(
                            scopeCats.length === 1
                              ? `${scopeCats[0]} Objectives`
                              : scopeCats.length > 1
                                ? 'Objectives (selected)'
                                : 'Objectives',
                            'objectives'
                          )}
                        </>
                      ) : (
                        <>
                          {sortTh('Mentor FB', 'mentorFb')}
                          {sortTh('Mentee FB', 'menteeFb')}
                        </>
                      )}
                      <th className="px-4 py-2.5 text-right font-semibold" aria-label="View objectives" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((e, i) => {
                      const cats = empCatMap[e.name] || [];
                      const shownCats = scopeCats.length > 0
                        ? cats.filter((c) => scopeCats.includes(c))
                        : cats;
                      const modalObjCount = scopedObjectives(e.name).length;
                      return (
                        <tr
                          key={i}
                          className="border-t transition-colors hover:bg-[#FAF9F6]"
                          style={{ borderColor: C.soft }}
                        >
                          {selectMode && (
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedNames.includes(e.name)}
                                onChange={() => toggleRowSel(e.name)}
                                title={`Select ${e.name} for export`}
                                style={{
                                  accentColor: C.ink,
                                  width: 16,
                                  height: 16,
                                  cursor: 'pointer',
                                }}
                              />
                            </td>
                          )}
                          <td className="px-4 py-3 text-[13px] font-semibold" style={{ color: C.ink }}>
                            {e.name}
                          </td>
                          <td className="px-4 py-3 text-[12.5px]" style={{ color: C.gray }}>
                            {e.designation}
                          </td>
                          <td className="px-4 py-3 text-[12.5px]" style={{ color: C.gray }}>
                            T{e.tier}
                          </td>
                          <td
                            className="px-4 py-3 text-[12.5px]"
                            style={{ color: hasMentor(e.mentor) ? C.gray : C.red }}
                          >
                            {hasMentor(e.mentor) ? e.mentor : 'Unassigned'}
                          </td>
                          {view === 'objectives' ? (
                            <>
                              <td className="px-4 py-3">
                                {shownCats.length > 0 ? (
                                  <span className="flex flex-wrap gap-1.5">
                                    {shownCats.map((c) => (
                                      <CatChip key={c} cat={c} />
                                    ))}
                                  </span>
                                ) : (
                                  <span style={{ color: C.grayFaint }}>—</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {(() => {
                                  const count =
                                    scopeCats.length > 0 ? modalObjCount : e.objectives;
                                  return count > 0 ? (
                                    <span className="text-[13px] font-semibold" style={{ color: C.ink }}>
                                      {count}
                                    </span>
                                  ) : (
                                    <Pill bg={C.amberSoft} text={C.amberText}>
                                      None yet
                                    </Pill>
                                  );
                                })()}
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-4 py-3">
                                {mentorLate(e) ? (
                                  <Pill bg={C.redSoft} text={C.red}>
                                    Late submission
                                  </Pill>
                                ) : (
                                  <BandPill status={hasMentor(e.mentor) ? e.mentorStatus : 'No mentor'} />
                                )}
                                {e.mentorSubmissionDate && (
                                  <div className="mt-1 text-[11px]" style={{ color: C.gray }}>
                                    Submitted {dayjs(e.mentorSubmissionDate).format('MMM DD YYYY')}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {menteeLate(e) ? (
                                  <Pill bg={C.redSoft} text={C.red}>
                                    Late submission
                                  </Pill>
                                ) : (
                                  <BandPill status={e.menteeStatus} />
                                )}
                                {e.menteeSubmissionDate && (
                                  <div className="mt-1 text-[11px]" style={{ color: C.gray }}>
                                    Submitted {dayjs(e.menteeSubmissionDate).format('MMM DD YYYY')}
                                  </div>
                                )}
                              </td>
                            </>
                          )}
                          <td className="px-4 py-3 text-right">
                            {modalObjCount > 0 ? (
                              <button
                                onClick={() => openObjModal(e)}
                                title={`View ${e.name}'s objectives`}
                                className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-semibold transition-colors hover:brightness-95"
                                style={{ background: C.soft, color: C.ink, border: 'none', cursor: 'pointer' }}
                              >
                                <EyeOutlined style={{ fontSize: 12 }} /> View
                              </button>
                            ) : (
                              <span style={{ color: C.grayFaint }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={selectMode ? 8 : 7} className="px-4 py-10 text-center text-sm" style={{ color: C.gray }}>
                          No one matches this filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
      <Modal
        open={!!objModal}
        onCancel={() => setObjModal(null)}
        footer={null}
        width={560}
        title={objModal ? `${objModal.name} — Objectives` : ''}
      >
        {objModal &&
          (objModal.objectives.length > 0 ? (
            <Tabs
              items={[
                ...CATEGORIES.filter((c) => objModal.objectives.some((o) => o.category === c)),
                ...Array.from(
                  new Set(
                    objModal.objectives
                      .map((o) => o.category)
                      .filter((c) => !CATEGORIES.includes(c))
                  )
                ),
              ].map((cat) => {
                const objs = objModal.objectives.filter((o) => o.category === cat);
                return {
                  key: cat,
                  label: `${cat} (${objs.length})`,
                  children: (
                    <div className="flex max-h-[380px] flex-col gap-2 overflow-y-auto pr-1">
                      {objs.map((o, i) => (
                        <div key={i} className="rounded-xl border p-3" style={{ borderColor: C.line }}>
                          <div className="flex items-start justify-between gap-3">
                            <span className="text-[13px]" style={{ color: C.ink }}>
                              {i + 1}. {o.description}
                            </span>
                            <span
                              className="shrink-0 text-[11px] font-semibold"
                              style={{ color: C.gray }}
                            >
                              <Status status={o.status} />
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ),
                };
              })}
            />
          ) : (
            <div className="py-6 text-center text-sm" style={{ color: C.gray }}>
              No objectives for {objModal.name}.
            </div>
          ))}
      </Modal>
      <NotificationAlert ref={myRef} />
    </div>
  );
};

export default FeedbackDashboard;
